import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Typography,
    Box
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';

interface GptSuggestionsDialogProps {
    open: boolean;
    suggestions: string[];
    onClose: () => void;
    onSelectSuggestion: (suggestion: string) => void;
}

const GptSuggestionsDialog: React.FC<GptSuggestionsDialogProps> = ({
    open,
    suggestions,
    onClose,
    onSelectSuggestion,
}) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);

    const handleListItemClick = (index: number) => {
        setSelectedIndex(index);
    };

    const handleSelectSuggestion = () => {
        onSelectSuggestion(suggestions[selectedIndex]);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6" fontWeight="bold">
                    Select a Suggestion
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    These suggestions are generated by GPT to help you get started or improve your message.
                </Typography>
            </DialogTitle>
            <DialogContent>
                {suggestions.length === 0 ? (
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                        <SentimentDissatisfiedIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1, mr: 2 }} />
                        <Typography color="text.secondary" fontWeight="medium">
                            No suggestions found
                        </Typography>
                    </Box>
                ) : (
                    <List>
                        {suggestions.map((suggestion, index) => (
                            <ListItem
                                button
                                key={index}
                                selected={selectedIndex === index}
                                onClick={() => handleListItemClick(index)}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    boxShadow: selectedIndex === index
                                        ? '0 2px 12px 2px rgba(76, 78, 100, 0.08)'
                                        : 'none',
                                    backgroundColor: selectedIndex === index ? 'primary.light' : 'grey.50',
                                    transition: 'background-color 0.2s, box-shadow 0.2s',
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ fontWeight: selectedIndex === index ? 'bold' : 'normal' }}>
                                            {suggestion}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSelectSuggestion}
                    color="primary"
                    disabled={selectedIndex === -1 || suggestions.length === 0}
                >
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GptSuggestionsDialog;
